import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import {
  errorMessage,
  OrderCreationMasterData,
  quantityCount,
} from '@helpers/Constants';
import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { useNavigate } from 'react-router-dom';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { MasterSpeciesProps, OrderSpecies } from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import { LicenseDetails } from '@components/Modal/LicenseDetails/LicenseDetails';
import { ConfirmationToast } from '@components/ConfirmationToast';

export const SpeciesSelection: React.FC = () => {
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const [selectedSpecies, setSelectedSpecies] = useState<number | null>(null);
  const [species, setSpecies] = useState<MasterSpeciesProps[]>([]);
  const [allSpecies, setAllSpecies] = useState<MasterSpeciesProps[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerId, setCustomerId] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [showLicenseForm, setShowLicenseForm] = useState<boolean>(false);
  const [showLicenseRequiredWarning, setShowLicenseRequiredWarning] =
    useState<boolean>(false);
  const [hasRestoredOrderSpecies, setHasRestoredOrderSpecies] =
    useState<boolean>(false);

  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId();
    if (customerID) {
      setCustomerId(customerID);
      const customerDetails = CustomerOrderService.getCustomerDetails();
      if (customerDetails?.id) {
        const parsedOrderData = CustomerOrderService.getParsedOrderData();
        const existingOrderData = parsedOrderData[customerID] ?? {};
        CustomerOrderService.setParsedOrderData({
          ...parsedOrderData,
          [customerID]: {
            ...existingOrderData,
            customerId: customerDetails.id,
            customerDetails,
          },
        });
      }
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
    fetchButcherSpecies();
  }, []);

  useEffect(() => {
    setHasRestoredOrderSpecies(false);
  }, [customerId]);

  useEffect(() => {
    if (
      !hasRestoredOrderSpecies &&
      species.length > 0 &&
      customerId
    ) {
      getOrderSpecies();
      setHasRestoredOrderSpecies(true);
    }
  }, [species.length, customerId, hasRestoredOrderSpecies]);

  const fetchButcherSpecies = async () => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.SPECIES_MASTER_DATA,
      });
      response.data.data.masterData.forEach((species: MasterSpeciesProps) => {
        species.quantity = 0;
      });
      setAllSpecies(response.data.data.masterData);
      setSpecies(response.data.data.masterData);
      localStorage.setItem(
        'speciesData',
        JSON.stringify(response.data.data.masterData),
      );
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const getOrderSpecies = (): void => {
    if (species.length === 0 || !customerId) return;
    const storedData = localStorage.getItem('orderData');
    if (storedData) {
      const orderData = JSON.parse(storedData);
      const customerOrder = orderData[customerId];
      if (customerOrder && customerOrder.speciesId && customerOrder.quantity) {
        const currentSelectedSpecies = species.find(
          (s) => s.id === customerOrder.speciesId,
        );
        const needsRestoration =
          selectedSpecies !== customerOrder.speciesId ||
          currentSelectedSpecies?.quantity !== customerOrder.quantity;

        if (needsRestoration) {
          setSelectedSpecies(customerOrder.speciesId);
          const updateSpeciesQuantity = (speciesList: MasterSpeciesProps[]) =>
            speciesList.map((s) =>
              s.id === customerOrder.speciesId
                ? { ...s, quantity: customerOrder.quantity }
                : { ...s, quantity: 0 },
            );
          setSpecies(updateSpeciesQuantity);
          setAllSpecies(updateSpeciesQuantity);
        }
      }
    }
  };

  const handleSelect = (id: number): void => {
    if (selectedSpecies === id) {
      setSelectedSpecies(null);
      setSpecies((prevSpecies) =>
        prevSpecies.map((species) =>
          species.id === id ? { ...species, quantity: 0 } : species,
        ),
      );
      return;
    }

    if (selectedSpecies !== null && selectedSpecies !== id) {
      setSpecies((prevSpecies) =>
        prevSpecies.map((s) =>
          s.id === selectedSpecies ? { ...s, quantity: 0 } : s,
        ),
      );
    }
    setSelectedSpecies(id);
    updateQuantity(id, quantityCount.increment);
  };

  // FIXED: updateQuantity now correctly deselects when quantity becomes zero
  const updateQuantity = (id: number, value: number): void => {
    // Find the current species to get its current quantity
    const currentSpecies = species.find(s => s.id === id);
    if (!currentSpecies) return;

    const newQuantity = Math.max(0, currentSpecies.quantity + value);

    // Update both the displayed and the full species lists
    setSpecies(prev =>
      prev.map(s => (s.id === id ? { ...s, quantity: newQuantity } : s))
    );
    setAllSpecies(prev =>
      prev.map(s => (s.id === id ? { ...s, quantity: newQuantity } : s))
    );

    // If quantity becomes zero, deselect the species (show "Select" buttons for all)
    if (newQuantity === 0) {
      setSelectedSpecies(null);
    }
  };

  const validateCustomerLicense = async (): Promise<boolean> => {
    try {
      const response = await Axios.post(
        ApiEndpoints.CUSTOMER_LICENSE_VALIDATE,
        {
          customerId: customerId,
        },
      );

      return response.data.data.isLicenseAvailable == 1;
    } catch (error) {
      showToastMessage(errorMessage.UNEXPECTED_ERROR, 'error');
      return false;
    }
  };

  const handleNext = async (): Promise<void> => {
    if (!selectedSpecies) {
      showToastMessage(
        errorMessage.SPECIES_SELECTION_VALIDATION_ERROR,
        'error',
      );
      return;
    }

    const selected = species.find((s) => s.id === selectedSpecies);

    if (!selected || selected.quantity <= 0) {
      showToastMessage(errorMessage.SPECIES_SELECTION_QUANTITY_ERROR, 'error');
      return;
    }

    const orderData = {
      customerId,
      customerDetails: CustomerOrderService.getCustomerDetails(),
      speciesId: selected.id,
      quantity: selected.quantity,
      speciesName: selected.name,
    };

    const existingOrderData = localStorage.getItem('orderData');
    const orderDataPayload = existingOrderData
      ? JSON.parse(existingOrderData)
      : {};

    const customerData = orderDataPayload[customerId] ?? null;

    if (!customerData) {
      orderDataPayload[customerId] = orderData;
    } else {
      let updatedSpecies: OrderSpecies[] =
        customerData.species && selected.id === customerData.speciesId
          ? [...customerData.species]
          : [];

      if (selected.quantity !== customerData.quantity) {
        updatedSpecies = updatedSpecies.slice(0, selected.quantity);
      }

      orderDataPayload[customerId] = {
        ...orderData,
        species: updatedSpecies,
      };
    }

    localStorage.setItem('currentCustomizingId', '0');
    localStorage.setItem('orderData', JSON.stringify(orderDataPayload));

    if (selected.requireLicenseFlag === 1) {
      const isLicenseValid = validateCustomerLicense();
      if ((await isLicenseValid) === false) {
        setShowLicenseRequiredWarning(true);
        return;
      }
    }
    navigate('species/customize');
  };

  return (
    <div>
      <div className="flex-1 overflow-auto relative">
        <div className="flex mt-4 p-1 items-center justify-between">
          <h2 className="text-xl font-semibold">Species</h2>
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              className="border p-1 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded-md"
              value={searchTerm}
              onChange={(e) => {
                const filterValue = e.target.value;
                setSearchTerm(filterValue);
                if (filterValue.trim() === '') {
                  const restoredSpecies = allSpecies.map((s) => {
                    const currentSpecies = species.find(
                      (current) => current.id === s.id,
                    );
                    return currentSpecies ? currentSpecies : s;
                  });
                  setSpecies(restoredSpecies);
                } else {
                  const filteredSpecies = allSpecies
                    .filter((s) =>
                      s.name.toLowerCase().includes(filterValue.toLowerCase()),
                    )
                    .map((s) => {
                      const currentSpecies = species.find(
                        (current) => current.id === s.id,
                      );
                      return currentSpecies ? currentSpecies : s;
                    });
                  setSpecies(filteredSpecies);
                }
              }}
            />
            <div className="absolute inset-y-0 right-2 flex items-center">
              <FontAwesomeIcon
                icon={faSearch}
                className="text-gray-500 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Full‑width container without max‑width restriction */}
        <div className="bg-white shadow-md rounded-md mb-16 px-4">
          {/* Auto‑fill grid – cards have a minimum width of 200px and expand to fill extra space */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-3 mb-4 mt-4">
            {species.map((species) => (
              <div
                key={species.id}
                className={`card shadow-md rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${selectedSpecies === species.id
                  ? 'shadow-lg bg-tertiary border border-primary'
                  : 'border-gray-800 hover:border-primary-400 hover:shadow-md'
                  }`}
              >
                {/* Image container with fixed height – keeps cards from being too tall */}
                <div className="w-full h-44 bg-white flex items-center justify-center overflow-hidden relative">
                  {!loading && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  <img
                    src={species.imageUrl}
                    alt={species.alt}
                    className={`w-full h-full object-cover ${!loading ? 'opacity-0' : 'opacity-100'
                      } transition-opacity duration-300`}
                    onLoad={() => setLoading(true)}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src =
                        '../../../Assets/SpeciesPlaceholder.png';
                      setLoading(true);
                    }}
                  />
                </div>
                {/* Content area */}
                <div className="px-3 pb-3 pt-1">
                  <p className="mt-1 font-medium text-lg text-center truncate">
                    {species.name}
                  </p>
                  {selectedSpecies === species.id ? (
                    <div className="mt-2 flex items-center justify-center rounded-md overflow-hidden">
                      <button
                        type="button"
                        className="bg-primary text-white w-10 h-10 flex items-center justify-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQuantity(species.id, quantityCount.decrement);
                        }}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <span className="w-14 text-center bg-white text-black font-semibold px-4 py-2">
                        {species.quantity}
                      </span>
                      <button
                        type="button"
                        className="bg-primary text-white w-10 h-10 flex items-center justify-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQuantity(species.id, quantityCount.increment);
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                  ) : (
                    <button
                      disabled={
                        selectedSpecies !== null && selectedSpecies !== species.id
                      }
                      className={`mt-2 px-4 py-2 rounded-md w-full ${selectedSpecies !== null && selectedSpecies !== species.id
                        ? 'bg-[#B4B4B4] text-gray-200 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary-500'
                        }`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(species.id);
                      }}
                    >
                      Select
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Buttons – full‑width container */}
      <div className="p-1 flex w-full gap-4 mt-2 mb-2">
        <div className="w-full flex items-center justify-between 2xl:justify-end gap-4">
          <button
            onClick={() => window.history.back()}
            type="button"
            className="w-1/2 2xl:w-[130px] h-10 px-6 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white transition-colors duration-200 flex items-center justify-center"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            type="submit"
            className="w-1/2 2xl:w-[150px] h-10 px-6 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center transition-colors duration-200"
          >
            Next
          </button>
        </div>
      </div>

      {showLicenseRequiredWarning && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="w-full max-w-md">
            <ConfirmationToast
              title="License Required"
              message="A valid license is required to proceed with this order. Please provide the customer license details to continue."
              onClose={() => setShowLicenseRequiredWarning(false)}
              onAction={() => {
                setShowLicenseRequiredWarning(false);
                setShowLicenseForm(true);
              }}
              buttonName="Add License"
              type="info"
            />
          </div>
        </div>
      )}
      {showLicenseForm && (
        <LicenseDetails onClose={() => setShowLicenseForm(false)} />
      )}
    </div>
  );
};